/**
 * Pending Absence — the logged-in employee's own list.
 * SRS: staff see only their own pending absences, with an awareness message and the count, and
 * resolve each day with "Correction" (reason + check-in/out times, goes to the approver) or
 * "Leave" (opens the Leave form). Days HR marked Authorized link straight to the Leave form.
 */
import React, { useCallback, useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { pendingAbsenceApi } from "../../utils/pendingAbsenceApi";
import LoadingIndicator from "../Common/components/LoadingIndicator";
import {
  ActionDialog,
  AwarenessBanner,
  DeadlineLabel,
  PageHeading,
  StatusBadge,
} from "./PendingAbsenceCommon";
import {
  GO_LIVE_NOTICE,
  buildLeaveUrl,
  displayStatus,
  formatDate,
  isAllSelected,
  itemKey,
  summarizeResults,
  toggleAll,
  toggleKey,
} from "./pendingAbsenceHelpers";

const canCorrect = (item) => item.can_employee_act;

function Details({ item }) {
  switch (item.status) {
    case "REFERRED_BACK":
      return (
        <span>
          Referred back{item.comment ? `: ${item.comment}` : "."}
        </span>
      );
    case "CORRECTION_REQUESTED":
      return (
        <span>
          Waiting for approval. Your reason: {item.request && item.request.reason}
        </span>
      );
    case "AUTHORIZED_ABSENCE":
      return (
        <span>
          HR authorized this absence. Please apply for leave.
          {item.comment ? ` Note: ${item.comment}` : ""}
        </span>
      );
    case "UNAUTHORIZED_ABSENCE":
      return (
        <span>
          HR marked this as Unauthorized Absence.
          {item.comment ? ` Note: ${item.comment}` : ""}
        </span>
      );
    case "APPROVED_PRESENCE":
    case "HR_RESOLVED_PRESENCE":
      return (
        <span>
          Resolved{item.resolved_by_name ? ` by ${item.resolved_by_name}` : ""}
          {item.resolved_date ? ` on ${formatDate(item.resolved_date.slice(0, 10))}` : ""}.
        </span>
      );
    default:
      return <span className="text-muted">No attendance or leave was recorded.</span>;
  }
}

export default function PendingAbsenceMine() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [data, setData] = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [dialogDates, setDialogDates] = useState(null); // dates being corrected, or null
  const [form, setForm] = useState({ reason: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await pendingAbsenceApi.get("/mine", {
        include_resolved: showResolved ? 1 : undefined,
      });
      setData(result);
      setSelected(new Set());
    } catch (error) {
      setLoadError(error.message);
    } finally {
      setLoading(false);
    }
  }, [showResolved]);

  useEffect(() => {
    load();
  }, [load]);

  const items = (data && data.items) || [];

  const openCorrection = (dates) => {
    setForm({ reason: "" });
    setDialogDates(dates);
  };

  const formError = () => (form.reason.trim() ? null : "Please enter a reason.");

  const submitCorrection = async () => {
    const error = formError();
    if (error) return toast.error(error);

    setBusy(true);
    try {
      const result = await pendingAbsenceApi.post("/mine/correction", {
        dates: dialogDates,
        reason: form.reason.trim(),
      });
      const summary = summarizeResults(result);
      if (summary.succeeded > 0) {
        toast.success(
          `${summary.succeeded} correction${summary.succeeded === 1 ? "" : "s"} sent for approval.`
        );
      }
      summary.failures.forEach((message) => toast.error(message, { autoClose: 8000 }));
      setDialogDates(null);
      await load();
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) return <LoadingIndicator marginTop={35} />;

  const selectedDates = items
    .filter((item) => selected.has(itemKey(item)))
    .map((item) => item.absence_date);

  return (
    <div>
      <ToastContainer position={toast.POSITION.TOP_RIGHT} />
      <PageHeading title="Pending Absence" />
      <div className="wrapper wrapper-content animated fadeInRight">
        {loadError && <div className="alert alert-danger">{loadError}</div>}

        {data && !data.live && <div className="alert alert-info">{GO_LIVE_NOTICE}</div>}

        {data && data.live && <AwarenessBanner pendingCount={data.pending_count} />}

        <div className="ibox float-e-margins">
          <div className="ibox-title" style={{ display: "flex", alignItems: "center" }}>
            <h5 style={{ flex: 1 }}>My pending absences</h5>
            <label style={{ marginRight: 15, marginBottom: 0, fontWeight: 400 }}>
              <input
                type="checkbox"
                checked={showResolved}
                onChange={(event) => setShowResolved(event.target.checked)}
              />{" "}
              Show resolved days
            </label>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={selectedDates.length === 0}
              onClick={() => openCorrection(selectedDates)}
            >
              Correction for selected ({selectedDates.length})
            </button>
          </div>
          <div className="ibox-content">
            {data && data.live && items.length === 0 && (
              <p className="text-center text-muted" style={{ margin: 20 }}>
                You have no pending absences.
              </p>
            )}

            {items.length > 0 && (
              <div className="table-responsive">
                <table className="table table-striped table-bordered table-hover">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          aria-label="Select all"
                          checked={isAllSelected(items, selected, canCorrect)}
                          onChange={() => setSelected(toggleAll(items, selected, canCorrect))}
                        />
                      </th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Details</th>
                      <th>Deadline</th>
                      <th style={{ width: 190 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={itemKey(item)}>
                        <td>
                          {canCorrect(item) && (
                            <input
                              type="checkbox"
                              aria-label={`Select ${item.absence_date}`}
                              checked={selected.has(itemKey(item))}
                              onChange={() => setSelected(toggleKey(selected, itemKey(item)))}
                            />
                          )}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>{formatDate(item.absence_date)}</td>
                        <td>
                          <StatusBadge status={displayStatus(item)} />
                        </td>
                        <td>
                          <Details item={item} />
                        </td>
                        <td>
                          <DeadlineLabel item={item} />
                        </td>
                        <td>
                          {canCorrect(item) && (
                            <>
                              <button
                                type="button"
                                className="btn btn-primary btn-xs"
                                style={{ marginRight: 5 }}
                                onClick={() => openCorrection([item.absence_date])}
                              >
                                Correction
                              </button>
                              <button
                                type="button"
                                className="btn btn-white btn-xs"
                                onClick={() => {
                                  window.location.href = buildLeaveUrl(item.absence_date);
                                }}
                              >
                                Leave
                              </button>
                            </>
                          )}
                          {item.status === "AUTHORIZED_ABSENCE" && (
                            <button
                              type="button"
                              className="btn btn-success btn-xs"
                              onClick={() => {
                                window.location.href = buildLeaveUrl(item.absence_date);
                              }}
                            >
                              Apply Leave
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <ActionDialog
        visible={dialogDates !== null}
        title={`Correction${dialogDates && dialogDates.length > 1 ? ` (${dialogDates.length} days)` : ""}`}
        confirmLabel="Send for approval"
        onClose={() => setDialogDates(null)}
        onConfirm={submitCorrection}
        busy={busy}
      >
        {dialogDates && (
          <p className="text-muted">
            {dialogDates.map((date) => formatDate(date)).join(", ")}
          </p>
        )}
        <div className="form-group">
          <label>Reason</label>
          <textarea
            className="form-control"
            rows={3}
            maxLength={500}
            value={form.reason}
            onChange={(event) => setForm({ ...form, reason: event.target.value })}
          />
        </div>
        <p className="text-muted" style={{ fontSize: 12 }}>
          Your manager will review this request. If it is approved, the day is counted as
          Presence.
        </p>
      </ActionDialog>
    </div>
  );
}
